import { validPage, validPosition, validSku } from './events';
import type { ObserveOptions } from './types';

const DEFAULT_SKU_ATTR = 'data-vsape-sku';
const DEFAULT_POSITION_ATTR = 'data-vsape-position';
const DWELL_MS = 500;
const VISIBILITY_THRESHOLD = 0.5;

export interface ObserverCallbacks {
  impression(sku: string, position: number, page?: number): void;
  click(sku: string, position: number, page?: number): void;
}

export class ResultObserver {
  private readonly callbacks: ObserverCallbacks;
  private observer: IntersectionObserver | undefined;
  private readonly states = new Map<Element, ElementState>();

  constructor(callbacks: ObserverCallbacks) {
    this.callbacks = callbacks;
  }

  observe(selector: string, options: ObserveOptions = {}): boolean {
    if (typeof document === 'undefined' || typeof IntersectionObserver === 'undefined') return false;
    if (!this.observer) {
      this.observer = new IntersectionObserver((entries) => this.handleIntersections(entries), {
        threshold: VISIBILITY_THRESHOLD
      });
    }

    let elements: NodeListOf<Element>;
    try {
      elements = document.querySelectorAll(selector);
    } catch {
      return false;
    }

    this.pruneDetachedStates();
    elements.forEach((element) => {
      this.observer?.observe(element);
      const existing = this.states.get(element);
      if (existing) {
        existing.options = { ...options };
        return;
      }
      const state: ElementState = {
        options: { ...options },
        timer: undefined,
        clickHandler: () => {
          const parsed = parseElement(element, state.options);
          if (parsed) this.callbacks.click(parsed.sku, parsed.position, parsed.page);
        }
      };
      element.addEventListener('click', state.clickHandler);
      this.states.set(element, state);
    });
    return true;
  }

  private pruneDetachedStates(): void {
    for (const [element, state] of this.states) {
      if (element.isConnected) continue;
      if (state.timer) clearTimeout(state.timer);
      this.observer?.unobserve(element);
      element.removeEventListener('click', state.clickHandler);
      this.states.delete(element);
    }
  }

  disconnect(): void {
    this.observer?.disconnect();
    this.observer = undefined;
    for (const [element, state] of this.states) {
      if (state.timer) clearTimeout(state.timer);
      element.removeEventListener('click', state.clickHandler);
    }
    this.states.clear();
  }

  private handleIntersections(entries: IntersectionObserverEntry[]): void {
    for (const entry of entries) {
      const state = this.states.get(entry.target);
      if (!state) continue;
      if (state.timer) clearTimeout(state.timer);

      if (!entry.isIntersecting || entry.intersectionRatio < VISIBILITY_THRESHOLD) {
        state.timer = undefined;
        continue;
      }

      state.timer = setTimeout(() => {
        const parsed = parseElement(entry.target, state.options);
        if (parsed) this.callbacks.impression(parsed.sku, parsed.position, parsed.page);
        state.timer = undefined;
      }, DWELL_MS);
    }
  }
}

interface ElementState {
  options: ObserveOptions;
  timer: ReturnType<typeof setTimeout> | undefined;
  clickHandler: EventListener;
}

function parseElement(
  element: Element,
  options: ObserveOptions
): { sku: string; position: number; page?: number } | undefined {
  try {
    const sku = element.getAttribute(options.skuAttr || DEFAULT_SKU_ATTR) || '';
    const position = Number(element.getAttribute(options.positionAttr || DEFAULT_POSITION_ATTR));
    const rawPage = options.pageAttr ? element.getAttribute(options.pageAttr) : null;
    const page = rawPage === null ? undefined : Number(rawPage);
    if (!validSku(sku) || !validPosition(position) || !validPage(page)) return undefined;
    return page === undefined ? { sku, position } : { sku, position, page };
  } catch {
    return undefined;
  }
}
